import { DataMode } from '../contexts/DataModeContext';

export const getFieldName = (baseField: string, dataMode: DataMode): string => {
  if (dataMode === 'accumulated') {
    // Try different variations of the accumulated field name
    const variations = [
      `${baseField} accumulated`,
      `${baseField} accumelated`, // Typo in actual data
      `${baseField}_accumulated`,
      `${baseField}_accumelated`,
      `${baseField}Accumulated`,
      `${baseField}Accumelated`
    ];
    return variations[0]; // Return the first variation, we'll handle fallbacks in getFieldValue
  }
  return baseField;
};

export const getBidVolumeField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Bid Volume accumelated' : 'Bid Volume';
};

export const getAskVolumeField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Ask Volume accumelated' : 'Ask Volume';
};

export const getBidCountField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Bid Count accumelated' : 'Bid Count';
};

export const getAskCountField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Ask Count accumelated' : 'Ask Count';
};

export const getTotalVolumeField = (dataMode: DataMode): string => {
  return 'Volume'; // Volume field is the same for both modes
};

export const getTradesField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Trades accumelated' : 'Trade Count';
};

export const getDeltaField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Delta accumelated' : 'Delta ( ask - bid )';
};

export const getDirectionField = (dataMode: DataMode): string => {
  return dataMode === 'accumulated' ? 'Direction accumelated' : 'Direction';
};

export const getSentimentField = (dataMode: DataMode): string => {
  return 'Sentiment';
};

export const getDeltaAskBidField = (dataMode: DataMode): string => {
  if (dataMode === 'accumulated') {
    return 'Delta accumelated';
  }
  return 'Delta ( ask - bid )';
};

export const getBandField = (dataMode: DataMode): string => {
  if (dataMode === 'accumulated') {
    return 'Band accumulated';
  }
  return 'Band';
};

export const getZahnField = (dataMode: DataMode): string => {
  if (dataMode === 'accumulated') {
    return 'Zahn accumulated';
  }
  return 'Zahn';
};

export const getHaltzField = (dataMode: DataMode): string => {
  if (dataMode === 'accumulated') {
    return 'Haltz accumulated';
  }
  return 'Haltz';
};

export const getOpenInterestField = (dataMode: DataMode): string => {
  return 'Open Interest'; // Open Interest field is the same for both modes
};

/** `getBidVolumeField` / `getBidCountField` return physical column names; `fieldMap` is keyed by logical names. */
const LEGACY_PHYSICAL_TO_LOGICAL: Record<string, string> = {
  'Bid Volume accumelated': 'Bid Volume',
  'Bid Volume accumulated': 'Bid Volume',
  'Ask Volume accumelated': 'Ask Volume',
  'Ask Volume accumulated': 'Ask Volume',
  'Bid Count accumelated': 'Bid Count',
  'Bid Count accumulated': 'Bid Count',
  'Ask Count accumelated': 'Ask Count',
  'Ask Count accumulated': 'Ask Count',
  'Delta accumelated': 'Delta',
  'Delta accumulated': 'Delta',
  'Delta ( ask - bid )': 'Delta',
  'Direction accumelated': 'Direction',
  'Direction accumulated': 'Direction',
  'Sentiment accumelated': 'Sentiment',
  'Sentiment accumulated': 'Sentiment',
  'Trades accumelated': 'Trades',
  'Trades accumulated': 'Trades',
  'Trade Count': 'Trades',
};

// Helper function to get field value with data mode support and fallbacks
export const getFieldValue = (item: any, fieldName: string, dataMode: DataMode): any => {
  // Use the actual data mode (both 5min and accumulated are now valid)
  const effectiveDataMode: DataMode = dataMode;
  const logicalFieldName = LEGACY_PHYSICAL_TO_LOGICAL[fieldName] || fieldName;

  const fieldMap: { [key: string]: (mode: DataMode) => string[] } = {
    'Bid Volume': (mode: DataMode) => mode === 'accumulated' 
      ? [
          'Bid Volume accumelated',
          'Bid Volume accumulated',
          'bid_volume_accumulated',
          'bid_volume_acc',
          'Bid Volume',
          'BidVolume',
          'bid_volume',
        ]
      : ['Bid Volume', 'BidVolume', 'bid_volume'],
    'Ask Volume': (mode: DataMode) => mode === 'accumulated'
      ? [
          'Ask Volume accumelated',
          'Ask Volume accumulated',
          'ask_volume_accumulated',
          'ask_volume_acc',
          'Ask Volume',
          'AskVolume',
          'ask_volume',
        ]
      : ['Ask Volume', 'AskVolume', 'ask_volume'],
    'Bid Count': (mode: DataMode) => mode === 'accumulated'
      ? [
          'Bid Count accumelated',
          'Bid Count accumulated',
          'bid_count_accumulated',
          'bid_count_acc',
          'Bid Count',
          'BidCount',
          'bid_count',
        ]
      : ['Bid Count', 'BidCount', 'bid_count'],
    'Ask Count': (mode: DataMode) => mode === 'accumulated'
      ? [
          'Ask Count accumelated',
          'Ask Count accumulated',
          'ask_count_accumulated',
          'ask_count_acc',
          'Ask Count',
          'AskCount',
          'ask_count',
        ]
      : ['Ask Count', 'AskCount', 'ask_count'],
    'Volume': (mode: DataMode) => mode === 'accumulated'
      ? ['Volume', 'Volume accumelated', 'Volume accumulated', 'Total Volume', 'total_volume']
      : ['Volume', 'Total Volume', 'total_volume'],
    'Trades': (mode: DataMode) => mode === 'accumulated'
      ? ['Trades accumelated', 'Trades accumulated', 'Trades', 'Total Trades', 'total_trades']
      : ['Trades', 'Total Trades', 'total_trades'],
    'Delta': (mode: DataMode) => mode === 'accumulated'
      ? ['Delta accumelated', 'Delta accumulated', 'Delta', 'delta']
      : ['Delta', 'Delta ( ask - bid )', 'delta'],
    'Gamma': (_mode: DataMode) => ['Gamma', 'gamma'],
    'Theta': (mode: DataMode) =>
      mode === 'accumulated'
        ? ['Theta accumelated', 'Theta accumulated', 'Theta', 'theta']
        : ['Theta', 'theta'],
    'Vega': (mode: DataMode) =>
      mode === 'accumulated'
        ? ['Vega accumelated', 'Vega accumulated', 'Vega', 'vega']
        : ['Vega', 'vega'],
    'Open Interest': (_mode: DataMode) => ['Open Interest', 'OpenInterest', 'open_interest', 'OI'],
    'Underlying Price': (_mode: DataMode) => ['Underlying Price', 'UnderlyingPrice', 'Underlying_Price', 'underlying_price'],
    'Strike': (_mode: DataMode) => ['Strike', 'Strike Price', 'strike'],
    'Direction': (mode: DataMode) => mode === 'accumulated'
      ? ['Direction accumelated', 'Direction accumulated', 'Direction']
      : ['Direction'],
    'Sentiment': (mode: DataMode) => mode === 'accumulated'
      ? ['Sentiment accumelated', 'Sentiment accumulated', 'Sentiment']
      : ['Sentiment'],
    'Band': (mode: DataMode) => mode === 'accumulated'
      ? ['optionsAwareBand', 'Band accumelated', 'Band accumulated', 'Band']
      : ['optionsAwareBand', 'Band'],
    'Zahn': (mode: DataMode) => mode === 'accumulated'
      ? ['optionsAwareZahn', 'Zahn accumelated', 'Zahn accumulated', 'Zahn']
      : ['optionsAwareZahn', 'Zahn'],
    'Haltz': (mode: DataMode) => mode === 'accumulated'
      ? ['Haltz accumelated', 'Haltz accumulated', 'Haltz']
      : ['optionsAwareHaltz', 'Haltz'],
    'MAL': (mode: DataMode) => mode === 'accumulated'
      ? ['optionsAwareMal', 'MAL accumulated', 'MAL']
      : ['optionsAwareMal', 'MAL'],
    // Core fields that widgets need
    'Underlying': (_mode: DataMode) => ['Underlying', 'underlying', 'underlyingSymbol', 'underlying_symbol'],
    'Symbol': (_mode: DataMode) => ['Symbol', 'symbol'],
    'Expiry Date': (_mode: DataMode) => ['Expiry Date', 'expiryDate', 'expiry_date', 'Expiry'],
    'Option Type': (_mode: DataMode) => ['Option Type', 'optionType', 'option_type', 'Side'],
    'Moneyness': (_mode: DataMode) => ['Moneyness', 'moneyness'],
    'Implied Volatility': (_mode: DataMode) => ['Implied Volatility', 'impliedVolatility', 'implied_volatility', 'IV']
  };

  const getFieldVariations = fieldMap[logicalFieldName];
  if (getFieldVariations) {
    const fieldVariations = getFieldVariations(effectiveDataMode);
    
    // Try each variation until we find a value
    for (const fieldVar of fieldVariations) {
      const value = item[fieldVar];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    
    // If no value found, return 0 for numeric fields
    if (
      [
        'Bid Volume',
        'Ask Volume',
        'Bid Count',
        'Ask Count',
        'Volume',
        'Trades',
        'Delta',
        'Gamma',
        'Theta',
        'Vega',
        'Open Interest',
      ].includes(logicalFieldName)
    ) {
      return 0;
    }
    return '';
  }

  // Fallback to direct field access (physical name first, then original key)
  const direct = item[fieldName];
  if (direct !== undefined && direct !== null && direct !== '') return direct;
  if (logicalFieldName !== fieldName) {
    const d2 = item[logicalFieldName];
    if (d2 !== undefined && d2 !== null && d2 !== '') return d2;
  }
  return 0;
};

/**
 * Stable/Theta-only: read Gamma, Open Interest, Underlying Price, Delta for gamma/GEX calculations.
 * No Polygon, no RTL, no "effective" mixing — only keys that Stable/Theta backend sends.
 * Tries canonical names first, then common API snake_case. Treats '-' as 0 (Stable sends '-' for missing).
 */
export const STABLE_GAMMA_FIELD_KEYS = ['Gamma', 'gamma'];
export const STABLE_OI_FIELD_KEYS = ['Open Interest', 'open_interest', 'OI', 'OpenInterest', 'Open_Interest', 'openInterest'];
export const STABLE_UNDERLYING_PRICE_KEYS = ['Underlying Price', 'UnderlyingPrice', 'underlying_price', 'Underlying_Price'];
export const STABLE_DELTA_KEYS = ['Delta', 'Delta ( ask - bid )', 'Delta accumelated', 'Delta accumulated', 'delta'];
/** Stable single Volume field only — do not use Bid Volume + Ask Volume for spot gamma/GEX. */
export const STABLE_VOLUME_FIELD_KEYS = [
  'Volume',
  'volume',
  'Volume accumelated',
  'Volume accumulated',
  'total_volume',
  'Total Volume',
  'day_volume',
  'Day Volume',
  'session_volume',
  'Session Volume',
];

function coerceNumeric(value: any): number {
  if (value === undefined || value === null || value === '' || value === '-') return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function getStableFieldValue(item: any, fieldName: 'Gamma' | 'Open Interest' | 'Underlying Price' | 'Delta' | 'Volume'): number {
  const keys =
    fieldName === 'Gamma' ? STABLE_GAMMA_FIELD_KEYS :
    fieldName === 'Open Interest' ? STABLE_OI_FIELD_KEYS :
    fieldName === 'Underlying Price' ? STABLE_UNDERLYING_PRICE_KEYS :
    fieldName === 'Volume' ? STABLE_VOLUME_FIELD_KEYS :
    STABLE_DELTA_KEYS;
  for (const k of keys) {
    const v = item[k];
    if (v !== undefined && v !== null && v !== '' && v !== '-') {
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

/**
 * True if record has any Gamma or Open Interest field from Stable/Theta keys.
 * Use this for filtering so spot gamma/GEX don't depend on RTL-only field names.
 */
export function recordHasGammaOrOI(record: any): boolean {
  if (!record || typeof record !== 'object') return false;
  for (const k of STABLE_GAMMA_FIELD_KEYS) {
    const v = record[k];
    if (v !== undefined && v !== null && v !== '') return true;
  }
  for (const k of STABLE_OI_FIELD_KEYS) {
    const v = record[k];
    if (v !== undefined && v !== null && v !== '') return true;
  }
  return false;
}

// Add data validation function
export const validateVolumeData = (bidVolume: number, askVolume: number, bidVolumeAcc: number, askVolumeAcc: number): boolean => {
  // Check if 5-minute data is reasonable compared to accumulated data
  const bidRatio = bidVolumeAcc > 0 ? bidVolume / bidVolumeAcc : 0;
  const askRatio = askVolumeAcc > 0 ? askVolume / askVolumeAcc : 0;
  
  // If 5-minute data is more than 10x higher than accumulated, it's corrupted
  if (bidRatio > 10 || askRatio > 10) {
    return false; // Data is corrupted
  }
  
  // If individual values are unreasonably high (> 100,000), it's corrupted
  if (bidVolume > 100000 || askVolume > 100000) {
    return false; // Data is corrupted
  }
  
  return true; // Data is valid
};

// Enhanced getFieldValue with corruption detection
export const getFieldValueSafe = (item: any, fieldName: string, dataMode: DataMode): any => {
  const value = getFieldValue(item, fieldName, dataMode);
  
  // For volume fields, check for corruption
  if (fieldName.includes('Bid Volume') || fieldName.includes('Ask Volume')) {
    const bidVolume = getFieldValue(item, 'Bid Volume', dataMode) || 0;
    const askVolume = getFieldValue(item, 'Ask Volume', dataMode) || 0;
    const bidVolumeAcc = getFieldValue(item, 'Bid Volume accumelated', dataMode) || 0;
    const askVolumeAcc = getFieldValue(item, 'Ask Volume accumelated', dataMode) || 0;
    
    // If 5-minute data is corrupted, fall back to accumulated data
    if (!validateVolumeData(bidVolume, askVolume, bidVolumeAcc, askVolumeAcc)) {
      if (fieldName === 'Bid Volume') {
        return bidVolumeAcc;
      } else if (fieldName === 'Ask Volume') {
        return askVolumeAcc;
      }
    }
  }
  
  return value;
};

// Type-safe numeric field getter
export const getNumericFieldValueSafe = (item: any, fieldName: string, dataMode: DataMode): number => {
  const value = getFieldValueSafe(item, fieldName, dataMode);
  return typeof value === 'number' ? value : 0;
}; 

// Utility function to clean and parse API data values
export const cleanApiValue = (value: any): any => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // If it's already a number, return it
  if (typeof value === 'number') {
    return value;
  }
  
  // If it's a string, try to parse it
  if (typeof value === 'string') {
    // Remove commas and try to convert to number
    const cleaned = value.replace(/,/g, '');
    
    // Handle special cases
    if (cleaned.toLowerCase() === 'nan' || cleaned.toLowerCase() === 'null' || cleaned.toLowerCase() === 'none') {
      return 0;
    }
    
    // Try to convert to number
    const parsed = parseFloat(cleaned);
    if (!isNaN(parsed)) {
      return parsed;
    }
    
    // If it's a boolean-like string, convert to number
    if (cleaned.toLowerCase() === 'true') return 1;
    if (cleaned.toLowerCase() === 'false') return 0;
    
    // If it's a valid string (like option type), return as-is
    return value;
  }
  
  // For other types, try to convert to number
  const converted = Number(value);
  return isNaN(converted) ? 0 : converted;
};

// Enhanced getFieldValue that cleans API data
export const getFieldValueClean = (item: any, fieldName: string, dataMode: DataMode): any => {
  const rawValue = getFieldValue(item, fieldName, dataMode);
  
  // For numeric fields, clean the value
  if (['Bid Volume', 'Ask Volume', 'Bid Count', 'Ask Count', 'Volume', 'Trades', 'Delta', 'Gamma', 'Theta', 'Vega', 'Open Interest', 'Strike', 'Underlying Price', 'Last', 'Mid', 'Theoretical Value'].includes(fieldName)) {
    return cleanApiValue(rawValue);
  }
  
  // For other fields, return as-is
  return rawValue;
};

/** Direct field accessor for CBOE-formatted ScannerData rows (no data-mode translation). */
export const getCBOEFieldValue = (item: Record<string, any>, fieldName: string): any => {
  return item[fieldName];
}; 